import React, { useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Component to automatically move the map to the selected country
function MapFocus({ coords }) {
  const map = useMap(); // Get the map instance

  useEffect(() => {
    if (coords) {
      // Smoothly fly to the selected coordinates with a zoom of 5
      map.flyTo(coords, 5, { duration: 1.5 });
    }
  }, [coords, map]);

  return null; // This component does not render anything visually
}

// Coordinates for European countries
// Key = country code, value = [latitude, longitude]
const countryCoords = {
  AT: [48.2082, 16.3738],
  BE: [50.8503, 4.3517],
  BG: [42.6977, 23.3219],
  CH: [46.9481, 7.4474],
  CY: [35.1856, 33.3823],
  DE: [52.52, 13.405],
  ES: [40.4168, -3.7038],
  FI: [60.1695, 24.9354],
  FR: [48.8566, 2.3522],
  GB: [51.5074, -0.1278],
  GR: [37.9838, 23.7275],
  HR: [45.815, 15.9785],
  HU: [47.4979, 19.0402],
  IE: [53.3331, -6.2489],
  IL: [31.0461, 34.8516],
  IS: [64.1355, -21.8954],
  IT: [41.9028, 12.4964],
  LT: [54.6892, 25.2798],
  LV: [56.9496, 24.1052],
  ME: [42.441, 19.2626],
  NL: [52.3676, 4.9041],
  NO: [59.9139, 10.7522],
  PL: [52.2297, 21.0122],
  PT: [38.7169, -9.1394],
  RS: [44.8176, 20.4569],
  SE: [59.3293, 18.0686],
  SI: [46.0569, 14.5058],
  SK: [48.669, 19.699],
};

// Main Europe Map component
function EuropeMap({ data, selectedCountry, selectedCondition, setSelectedCountry, countryNames }) {
  // Get coordinates for the selected country
  const selectedCoords = selectedCountry
    ? countryCoords[selectedCountry]
    : null;

  return (
    <div className="map-section">
      <MapContainer
        center={[54, 15]} // Center of Europe
        zoom={4}
        minZoom={3}
        maxZoom={6}
        maxBounds={[
          // Restrict map to Europe bounds
          [34, -25],
          [72, 45],
        ]}
        className="leaflet-container"
      >
        {/* Base map tiles */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* Automatically focus on the selected country */}
        <MapFocus coords={selectedCoords} />

        {/* Render markers for each country */}
        {data.map((d) => {
          const coords = countryCoords[d.cntr]; // Get coordinates
          if (!coords) return null; // Skip if no coordinates

          const isSelected = selectedCountry === d.cntr; // Highlight selected country

          const hlrValue = Number(d.hl_r || d.hlr);
          const giniValue = Number(d.ginsum);

          return (
            <CircleMarker
              key={d.cntr}
              center={coords}
              radius={isSelected ? 12 : 8} // Larger marker for selected country
              pathOptions={{
                fillColor: isSelected ? "red" : "gray",
                color: isSelected ? "#333" : "#eee",
                weight: 2,
                fillOpacity: 0.9,
              }}
              eventHandlers={{
                // set the clicked country in the dropdown selector
                click: () => {
                    setSelectedCountry(d.cntr);
                }
              }}
            >
              {/* Show different popup depending on whether condition is selected */}
              {selectedCondition ? (
                <Popup className="map-popup">

                  <h4 className="map-popup-country-name">
                    {countryNames[d.cntr]}
                  </h4>

                  {selectedCondition && (
                    <div className="popup-content">
                      <div>
                        Highest to Lowest Ratio:
                        <div className="popup-stat-value">
                          {isNaN(hlrValue) ? "N/A" : hlrValue.toFixed(1)}×
                        </div>
                      </div>
                      <div>
                        Gini coefficient:{" "}
                        <div className="popup-stat-value">
                          {isNaN(giniValue) ? "N/A" : giniValue.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  )}
                </Popup>
              ) : (
                <Popup>
                  <h4>{countryNames[d.cntr]}</h4>
                </Popup>
              )}
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}

export default EuropeMap;
